import { useQuery } from "react-query";

import axios from "axios";

import { useUser } from "hooks";
import { BetaFeatures } from "hooks/useBetaFeatures";

import { compareUserTypeWellProductsWithDefaultTypeWellProducts } from "components/arps/utils/compareUserTypeWellProductsWithDefaultTypeWellProducts";

import { ApiResult } from "./apiResult";
import { ITypeWellProductSettings, ITypeWellSettings } from "./typeWell";

export const defaultTypeWellProductSettingsInNominal: ITypeWellProductSettings[] = [
  {
    product: "GAS",
    headers: ["Q0", "tramp", "Qi", "Di", "Btrans", "ttrans", "Bhyp", "Df", "Bf", "Qf"],
    displayHeaders: [
      "Q.0",
      "t.ramp",
      "Q.i",
      "D.i",
      "B.trans",
      "t.trans",
      "B.hyp",
      "D.f",
      "B.f",
      "Q.f"
    ],
    values: {
      Q0: 0,
      tramp: 2,
      Qi: 5000,
      Di: 1.203972804325936,
      Btrans: 1.2,
      ttrans: 12,
      Bhyp: 1,
      Df: 0.08338160893905101,
      Bf: 0,
      Qf: 50
    }
  },
  {
    product: "OIL",
    headers: ["Q0", "tramp", "Qi", "Di", "Btrans", "ttrans", "Bhyp", "Df", "Bf", "Qf"],
    displayHeaders: [
      "Q.0",
      "t.ramp",
      "Q.i",
      "D.i",
      "B.trans",
      "t.trans",
      "B.hyp",
      "D.f",
      "B.f",
      "Q.f"
    ],
    values: {
      Q0: 0,
      tramp: 2,
      Qi: 250,
      Di: 1.203972804325936,
      Btrans: 1.2,
      ttrans: 12,
      Bhyp: 1,
      Df: 0.08338160893905101,
      Bf: 0,
      Qf: 5
    }
  },
  {
    product: "CONDENSATE",
    headers: ["Q0", "tramp", "Qi", "Di", "Btrans", "ttrans", "Bhyp", "Df", "Bf", "Qf"],
    displayHeaders: [
      "Q.0",
      "t.ramp",
      "Q.i",
      "D.i",
      "B.trans",
      "t.trans",
      "B.hyp",
      "D.f",
      "B.f",
      "Q.f"
    ],
    values: {
      Q0: 0,
      tramp: 2,
      Qi: 250,
      Di: 1.203972804325936,
      Btrans: 1.2,
      ttrans: 12,
      Bhyp: 1,
      Df: 0.08338160893905101,
      Bf: 0,
      Qf: 5
    }
  },
  {
    product: "WATER",
    headers: ["Q0", "tramp", "Qi", "Di", "Btrans", "ttrans", "Bhyp", "Df", "Bf", "Qf"],
    displayHeaders: [
      "Q.0",
      "t.ramp",
      "Q.i",
      "D.i",
      "B.trans",
      "t.trans",
      "B.hyp",
      "D.f",
      "B.f",
      "Q.f"
    ],
    values: {
      Q0: 0,
      tramp: 2,
      Qi: 250,
      Di: 1.203972804325936,
      Btrans: 1.2,
      ttrans: 12,
      Bhyp: 1,
      Df: 0.08338160893905101,
      Bf: 0,
      Qf: 5
    }
  },
  {
    product: "SALES GAS",
    headers: ["Q0", "tramp", "Qi", "Di", "Btrans", "ttrans", "Bhyp", "Df", "Bf", "Qf"],
    displayHeaders: [
      "Q.0",
      "t.ramp",
      "Q.i",
      "D.i",
      "B.trans",
      "t.trans",
      "B.hyp",
      "D.f",
      "B.f",
      "Q.f"
    ],
    values: {
      Q0: 0,
      tramp: 2,
      Qi: 5000,
      Di: 1.203972804325936,
      Btrans: 1.2,
      ttrans: 12,
      Bhyp: 1,
      Df: 0.08338160893905101,
      Bf: 0,
      Qf: 50
    }
  },
  {
    // STEAM is intentionally last — it must always render as the final product column.
    product: "STEAM",
    headers: ["Q0", "tramp", "Qi", "Di", "Btrans", "ttrans", "Bhyp", "Df", "Bf", "Qf"],
    displayHeaders: [
      "Q.0",
      "t.ramp",
      "Q.i",
      "D.i",
      "B.trans",
      "t.trans",
      "B.hyp",
      "D.f",
      "B.f",
      "Q.f"
    ],
    values: {
      Q0: 0,
      tramp: 18,
      Qi: 300,
      Di: 0.22314355,
      Btrans: 0.01,
      ttrans: 48,
      Bhyp: 0.01,
      Df: 0.22314355,
      Bf: 0,
      Qf: 50
    }
  }
];

/**
 * Filter default type well product settings based on beta features.
 * @param hasFeature Function to check if a beta feature is enabled
 * @returns Filtered array of type well product settings
 */
export function getFilteredDefaultTypeWellProducts(
  hasFeature: (feature: BetaFeatures) => boolean
): ITypeWellProductSettings[] {
  return defaultTypeWellProductSettingsInNominal.filter((product) => {
    // Filter out STEAM if "Forecast Steam" beta feature is not enabled
    if (product.product === "STEAM" && !hasFeature("Forecast Steam")) {
      return false;
    }
    return true;
  });
}

///hook to fetch default type wells
const userRoot = process.env.REACT_APP_USER_SERVICE;
export default function useTypeWellSettings(): ApiResult<ITypeWellSettings> {
  const user = useUser();
  const hasFeature = (feature: BetaFeatures) => {
    return user?.user?.organization?.betaFeatures?.includes(feature) ?? false;
  };
  const filteredDefaults = getFilteredDefaultTypeWellProducts(hasFeature);

  const { data, isLoading, isError, refetch } = useQuery<ITypeWellSettings>(
    "fetchDefaultTypeWells",
    async () => {
      try {
        const response = await axios.get(`${userRoot}/user/setting/DefaultTypeWell/`);

        if (
          response.status === 200 &&
          response?.data?.length &&
          response.data[0].settings?.length
        ) {
          const missingProducts = compareUserTypeWellProductsWithDefaultTypeWellProducts(
            filteredDefaults,
            response
          );
          missingProducts.forEach((missingProduct) => {
            const missingProductSettings = filteredDefaults.find(
              (defaultTypeWellProduct) =>
                defaultTypeWellProduct.product === missingProduct
            );
            response.data[0].settings.push(missingProductSettings);
          });

          return response.data[0];
        } else {
          return {
            id: "DefaultTypeWell",
            settings: filteredDefaults
          } as ITypeWellSettings;
        }
      } catch (err) {
        return {
          id: "DefaultTypeWell",
          settings: filteredDefaults
        } as ITypeWellSettings;
      }
    },
    {
      enabled: false,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (axios.isCancel(error)) {
          return;
        }
      }
    }
  );

  return {
    data,
    isLoading,
    isError,
    refetch
  };
}
